import React, { useCallback, useEffect, useState } from "react";

import moment, { Moment } from "moment";

import { useGetFieldPractices, useGetPracticeCrops, useTranslation } from "utils";

import { CROP_ROTATION, CropItemData, MainCropSettingsCMC, TimelineCalendarItemsProps } from "models";

import { CheckboxSegmented, Flex, Form, Icon, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider.tsx";
import { CMC_ONBOARDING_1_ERRORS } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { MainCropSelectionPopover } from "pages/app/crop-mix-composer/components/MainCropSelectionPopover.tsx";
import { getCropsBeforeAndAfter } from "pages/app/crop-mix-composer/utils/cropMixComposer";

export type AddEditCropsFormValues = {
  firstCrop: {
    cropId?: string;
    date?: Moment;
  };
  secondCrop: {
    cropId?: string;
    date?: Moment;
  };
};

export const AddEditCropsSection = ({
  selectedRecommendationItemOnTimeline,
  fieldId,
}: {
  selectedRecommendationItemOnTimeline?: TimelineCalendarItemsProps<CropItemData>;
  fieldId?: string;
}) => {
  const { t } = useTranslation("cropMixComposer");
  const {
    setMainCropSettings,
    mainCropSettings,
    cropRotationFamiliesToExclude,
    setCropRotationFamiliesToExclude,
    setLegumeExclusionConfirmation,
    isChangingStep,
    onboarding1Errors,
  } = useCropMixComposerContext();

  // this includes both enabled and disabled crops, only use it for checking crop families and other properties, NOT as a list for the crop selection component
  const { data: allCrops } = useGetPracticeCrops({});
  const { data: practicesResponse, isFetching: isFetchingPractices } = useGetFieldPractices({ fieldId: fieldId || "", inView: !!fieldId });

  const [form] = Form.useForm<AddEditCropsFormValues>();
  const [weeks, setWeeks] = useState<number>();

  const handleWeeks = useCallback(({ harvestDate, seedingDate }: { harvestDate: Moment; seedingDate: Moment }) => {
    const weeks = seedingDate.diff(harvestDate, "days") / 7;
    setWeeks(Math.round(weeks));
  }, []);

  const mainCropsBeforeAndAfterRecommendation = getCropsBeforeAndAfter(practicesResponse, selectedRecommendationItemOnTimeline);

  const shouldShowErrorSpacer = [
    CMC_ONBOARDING_1_ERRORS.MAIN_CROP_1,
    CMC_ONBOARDING_1_ERRORS.MAIN_CROP_2,
    CMC_ONBOARDING_1_ERRORS.SEEDING_BEFORE_HARVEST,
    CMC_ONBOARDING_1_ERRORS.MINIMUM_GROWING_PERIOD,
  ].some((error) => onboarding1Errors.includes(error));

  const handleFinish = async () => {
    if (isChangingStep !== null) return;

    const cropId1 = (form.getFieldValue(["firstCrop", "cropId"]) as string | undefined) || mainCropSettings?.crop1Id;
    const cropId2 = (form.getFieldValue(["secondCrop", "cropId"]) as string | undefined) || mainCropSettings?.crop2Id;
    const harvestDate =
      (form.getFieldValue(["firstCrop", "date"]) as Moment | undefined) || (mainCropSettings?.harvestDate ? moment(mainCropSettings.harvestDate) : undefined);
    const seedingDate =
      (form.getFieldValue(["secondCrop", "date"]) as Moment | undefined) || (mainCropSettings?.seedingDate ? moment(mainCropSettings.seedingDate) : undefined);

    const oldCrop1Family = cropId1 ? allCrops?.find((crop) => crop.id === mainCropSettings?.crop1Id)?.cropFamily || null : null;
    const oldCrop2Family = cropId2 ? allCrops?.find((crop) => crop.id === mainCropSettings?.crop2Id)?.cropFamily || null : null;
    const newCrop1Family = form.getFieldValue(["firstCrop", "cropId"])
      ? allCrops?.find((crop) => crop.id === form.getFieldValue(["firstCrop", "cropId"]))?.cropFamily || null
      : null;
    const newCrop2Family = form.getFieldValue(["secondCrop", "cropId"])
      ? allCrops?.find((crop) => crop.id === form.getFieldValue(["secondCrop", "cropId"]))?.cropFamily || null
      : null;

    const updatedCropRotationFamiliesToExclude = [...cropRotationFamiliesToExclude].filter((family) => family !== oldCrop1Family && family !== oldCrop2Family);
    if (newCrop1Family && !updatedCropRotationFamiliesToExclude.includes(newCrop1Family)) {
      updatedCropRotationFamiliesToExclude.push(newCrop1Family);
    }
    if (newCrop2Family && !updatedCropRotationFamiliesToExclude.includes(newCrop2Family)) {
      updatedCropRotationFamiliesToExclude.push(newCrop2Family);
    }

    if (!updatedCropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE)) {
      setLegumeExclusionConfirmation(null);
    }

    setCropRotationFamiliesToExclude(updatedCropRotationFamiliesToExclude);

    if (seedingDate && harvestDate) {
      handleWeeks({ seedingDate, harvestDate });
    }

    const updatedMainCropsPreferencesCMC: MainCropSettingsCMC = {
      crop1Id: cropId1,
      crop2Id: cropId2,
      seedingDate: seedingDate?.toISOString(),
      harvestDate: harvestDate?.toISOString(),
    };
    setMainCropSettings(updatedMainCropsPreferencesCMC);
  };

  useEffect(() => {
    if (mainCropSettings?.seedingDate && mainCropSettings?.harvestDate) {
      handleWeeks({ seedingDate: moment(mainCropSettings.seedingDate), harvestDate: moment(mainCropSettings.harvestDate) });
    }
  }, [mainCropSettings, handleWeeks]);

  return (
    <Form onFinish={handleFinish} name={"crop-mix-composer-main-crop-selection"} form={form} className="crop-mix-composer-add-edit-crop-section-form">
      <Flex align="center" gap="1rem">
        <MainCropSelectionPopover
          type="firstCrop"
          cropId={mainCropSettings?.crop1Id}
          date={mainCropSettings?.harvestDate}
          disabled={isFetchingPractices || (!!fieldId && !!mainCropsBeforeAndAfterRecommendation.before?.cropId)}
          loading={!!fieldId && isFetchingPractices}
          shouldShowErrorSpacer={shouldShowErrorSpacer}
        />
        <Icon iconName="tabler/arrow-narrow-right" className="arrow-icon" />
        <Flex vertical={true} justify="center" align="center" gap={"0.5rem"}>
          <div>
            <CheckboxSegmented className="crop-mix-segment" disabled label={t("cropAndSoilConditions.cropSelection.yourCoverCropMix")} />
            {shouldShowErrorSpacer && <span className="cmc-onboarding-step-error-message" />}
          </div>
          {weeks ? (
            <Typography variant="paragraph-s" data-testid={"crop-mix-composer-main-crop-selection-weeks"} className="margin-0 growth-time-text">
              {t("cropAndSoilConditions.cropSelection.growthPeriod", { weeks: weeks })}
            </Typography>
          ) : (
            <span className="crop-mix-composer-selection-spacer" />
          )}
        </Flex>
        <Icon iconName="tabler/arrow-narrow-right" className="arrow-icon" />
        <MainCropSelectionPopover
          type="secondCrop"
          cropId={mainCropSettings?.crop2Id}
          date={mainCropSettings?.seedingDate}
          disabled={isFetchingPractices || (!!fieldId && !!mainCropsBeforeAndAfterRecommendation.after?.cropId)}
          loading={!!fieldId && isFetchingPractices}
          shouldShowErrorSpacer={shouldShowErrorSpacer}
        />
      </Flex>
    </Form>
  );
};
